import { addDays, isSameDay } from "date-fns";
import React, { useMemo, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "semantic-ui-react";

import "components/DatePicker/DatePicker.scss";
import { addDaysToDate, formatDate } from "utils/dateHelperFunctions";

type BaseDatePickerProps = {
  minDate?: Date;
  fluid?: boolean;
  disabled?: boolean;
  withPortal?: boolean;
  showMonthDropdown?: boolean;
  showYearDropdown?: boolean;
  showLabel?: boolean;
  selectsRange?: any;
  date?: Date | null;
  clearable?: boolean;
  withTodayButton?: boolean;
  withWrapper?: boolean;
  customInput?: React.ReactElement<unknown, string | React.JSXElementConstructor<any>> | undefined;
  customDatePickerStyle?: string;
  onClear?: () => void;
};

interface SingleDatePickerProps extends BaseDatePickerProps {
  selectsRange?: false;
  onDateChange?: (...dates: (Date | null)[]) => void;
  startDate?: never;
  endDate?: never;
  date: Date | null;
  maxRange?: number;
  maxRangeDays?: never;
}

interface RangeDatePickerProps extends BaseDatePickerProps {
  selectsRange: any;
  onDateChange?: (...dates: (Date | null)[]) => void;
  startDate: Date | null;
  endDate: Date | null;
  maxRange?: number;
  maxRangeDays?: number;
}

type DatePickerProps = SingleDatePickerProps | RangeDatePickerProps;

export enum DATE_FORMATS_FNS {
  dateMonthYear = "dd-MM-yyyy",
  dateMonthYearSlash = "dd/MM/yyyy",
  dateMonthYearRange = "dd-MM-yyyy - dd-MM-yyyy",
  dateMonthYearTime = "dd-MM-yyyy HH:mm",
  timeDateMonthYearShort = "HH:mm dd-MM-yy",
  timeDateMonthYear = "HH:mm dd-MM-yyyy",
  yearMonthDateSlash = "yyyy/MM/dd",
  yearMonthDate = "yyyyMMdd",
  timeCarAppDate = "yyyy-MM-dd'T'12:00:00'Z'",
  ISOFormat = "yyyy-MM-dd'T'HH:mm:ssXXX",
  ISOFormatStartOfDay = "yyyy-MM-dd'T'00:00:00XXX",
  ISOFormatEndOfDay = "yyyy-MM-dd'T'23:59:59XXX",
  ISOFormatStartOfDayWithZ = "yyyy-MM-dd'T'00:00:00'Z'",
  ISOFormatEndOfDayWithZ = "yyyy-MM-dd'T'23:59:59'Z'",
  ISOFormatWithZ = "yyyy-MM-dd'T'HH:mm:ss'Z'",
  hoursMinutes = "HH:mm",
  hoursMinutesSeconds = "HH:mm:ss",
  dateMonthYearShort = "dd-MM-yy",
  yearMonthDateDash = "yyyy-MM-dd",
  yearMonthDatehoursMinutes = "yyyy-MM-dd HH:mm",
  dateMonthHoursMinutes = "dd-MM HH:mm",
  monthDayCommaYear = "MMM dd, yyyy",
  dateMonthYearDot = "dd.MM.yyyy",
  dateMonthYearDotAtTime = "dd.MM.yyyy 'at' HH:mm",
  monthDayYear = "MM-dd-yyyy",
  dateMonthYearTimeWithSeconds = "dd-MM-yyyy HH:mm:ss"
}

export const DatePicker = ({
  onDateChange,
  date,
  fluid,
  disabled,
  withPortal,
  showLabel,
  showMonthDropdown = false,
  showYearDropdown = false,
  selectsRange = false,
  startDate,
  endDate,
  maxRange,
  clearable,
  withTodayButton,
  customInput,
  customDatePickerStyle,
  maxRangeDays,
  minDate,
  onClear
}: DatePickerProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState<boolean>(false);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);

  const selectedDate = useMemo(() => {
    if ((selectsRange && startDate && endDate) || date) setOpen(false);

    if (selectsRange && startDate && endDate) {
      if (isSameDay(startDate, endDate)) {
        if (isSameDay(startDate, new Date())) return t("v8_today", "Today");
        return formatDate(startDate, DATE_FORMATS_FNS.dateMonthYear);
      }

      const startStr = formatDate(startDate, DATE_FORMATS_FNS.dateMonthYear);
      const endStr = formatDate(endDate, DATE_FORMATS_FNS.dateMonthYear);
      return `${startStr} - ${endStr}`;
    }

    if (date) {
      if (isSameDay(date, new Date())) return t("v8_today", "Today");
      return formatDate(date, DATE_FORMATS_FNS.dateMonthYear);
    }

    if (selectsRange) return t("v8_select_date_range", "Select date range");
    return t("v8_select_date", "Select date");
  }, [date, startDate, endDate, selectsRange]);

  const isDateSelectable = (date: Date): boolean => {
    if (!selectedStartDate) return true;

    const maxEndDate = addDays(selectedStartDate, maxRangeDays || 0);
    return isSameDay(date, selectedStartDate) || (date > selectedStartDate && date <= maxEndDate);
  };

  const handleChange = (dates: Date | [Date | null, Date | null] | null) => {
    if (!dates) return;

    if (Array.isArray(dates)) {
      const [start, end] = dates;

      if (maxRangeDays) {
        if (!end && start) setSelectedStartDate(start);
        else if (start && end) setSelectedStartDate(null);
      }

      if (onDateChange) onDateChange(start, end);
      if (start && end) setOpen(false);
    } else {
      if (onDateChange) onDateChange(dates);
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;

    if (onClear) {
      setSelectedStartDate(null);
      onClear();
      return;
    }

    if (!selectsRange) {
      if (onDateChange) onDateChange(null);
      return;
    }

    setSelectedStartDate(null);
    if (onDateChange) onDateChange(null, null);
  };

  return (
    <>
      <div
        className={`datepicker-wrapper ${fluid ? "fluid" : ""} ${disabled ? "disabled" : ""}  ${customDatePickerStyle ? customDatePickerStyle : ""}`}
        onClick={() => (disabled ? null : setOpen(true))}
      >
        {showLabel && (
          <>
            <div className="datepicker-row">
              <p className="label">{t("v8_date", "Date")}</p>
              <p className="date">{selectedDate}</p>
            </div>
            <div>
              <Icon name="dropdown" />
            </div>
          </>
        )}
        <ReactDatePicker
          minDate={minDate}
          calendarClassName={`CalendarDatepicker `}
          popperProps={{ strategy: "fixed" }}
          open={open}
          disabled={disabled}
          onClickOutside={() => setOpen(false)}
          todayButton={withTodayButton ? <span>{t("v8_today", "Today")}</span> : null}
          calendarStartDay={1}
          showPopperArrow={false}
          placeholderText={selectsRange ? t("v8_select_date_range", "Select date range") : t("v8_select_date", "Select date")}
          selected={selectsRange ? endDate : date}
          onChangeRaw={event => event?.preventDefault()}
          onChange={handleChange}
          withPortal={!!withPortal}
          showMonthDropdown={showMonthDropdown}
          showYearDropdown={showYearDropdown}
          selectsRange={selectsRange}
          startDate={startDate ?? undefined}
          endDate={endDate ?? undefined}
          maxDate={maxRange && startDate ? addDaysToDate(startDate, maxRange) : undefined}
          dateFormat="dd-MM-yyyy"
          timeFormat="12:00:00"
          customInput={customInput}
          shouldCloseOnSelect={!selectsRange}
          disabledKeyboardNavigation
          {...(maxRangeDays && { filterDate: isDateSelectable })}
        >
          {clearable && (
            <div className="react-datepicker-clear-date-container">
              <Button
                content={selectsRange ? t("v8_clear_date_range", "Clear date range") : t("v8_clear_date", "Clear date")}
                icon="calendar"
                labelPosition="left"
                onClick={handleClear}
                className="react-datepicker-clear-date-button"
              />
            </div>
          )}
        </ReactDatePicker>
      </div>
    </>
  );
};
